import React from 'react'
import PropTypes from 'prop-types'
import DefaultLayout from 'js/components/DefaultLayout'
import withSSR from 'js/components/withSSR/withSSR'
import classNames from 'classnames'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import { useInView } from 'react-intersection-observer'
import loadable from '@loadable/component'
import deepmerge from 'deepmerge'
import MealPlansPageHeader from 'js/pages/MealPlansPage/MealPlansPageHeader'
import { useLocation } from 'react-router-dom'
import convert from 'htmr'
import MealPlansPageAppPromo from 'js/pages/MealPlansPage/MealPlansPageAppPromo'
import MealPlansPageFAQ from 'js/pages/MealPlansPage/MealPlansPageFAQ'
import PageBottomHero from 'js/pages/MealPlansPage/PageBottomHero'
import PageBlock from 'js/pages/DatoPage/PageBlock'
import HashAnchor from 'js/components/HashAnchor/HashAnchor'
import { runQuery } from 'js/dato'
import mealPlansQuery from 'js/pages/MealPlansPage/mealPlansQuery'
import datoLocales from 'js/datoLocales'

const Footer = loadable(() => import('js/components/Footer/Footer'))

function DatoPage({ data, isLoading, error }) {
  const [belowFoldRef, scrolledBelowFold] = useInView({ triggerOnce: true })
  const location = useLocation()
  const nativeAd =
    new URLSearchParams(location.search).get('native-ad') === 'true'

  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage error={error} />
  }

  const pageData = deepmerge(data.fallback, data.page, {
    customMerge(key) {
      if (data.page[key] === '') return data.fallback[key]
    },
    arrayMerge: function (destinationArray, sourceArray) {
      sourceArray

      return sourceArray.map((section, index) => {
        if (section.sectionBlocks && section.sectionBlocks.length === 0) {
          return destinationArray[index]
        }
        return section
      })
    },
  })

  const gradient = `linear-gradient(to right, ${[
    pageData.gradientFrom.hex,
    pageData.gradientTo.hex,
  ].join(',')})`

  return (
    <DefaultLayout>
      <div className="bg-BgSub">
        <MealPlansPageHeader
          nativeAd={nativeAd}
          backgroundStyles={{
            backgroundImage: gradient,
          }}
          heading={convert(pageData.heading)}
          screenshot={pageData.headerScreenshot.url}
          firstSectionBackground={
            pageData.sections && pageData.sections[0].background === 'secondary'
              ? 'bg-BgSub'
              : 'bg-Bg'
          }
        />
        <HashAnchor id="start">
          {pageData.sections &&
            pageData.sections.map((section, index) => {
              return (
                <div
                  key={section.id}
                  ref={index === pageData.sections.length - 1 && belowFoldRef}
                  className={classNames(
                    section.background === 'secondary' ? 'bg-BgSub' : 'bg-Bg',
                    'py-40',
                  )}
                >
                  <div className="mx-auto w-full max-w-1280 xl:max-w-1440">
                    {section.heading && (
                      <h2 className="type-large mx-auto mb-24 max-w-968 px-12 text-center leading-default last:mb-0 md:mb-32 md:px-24">
                        {section.heading}
                      </h2>
                    )}
                    {section.sectionBlocks.map(
                      ({ id, __typename: type, ...props }) => {
                        return (
                          <PageBlock
                            key={id}
                            type={type}
                            width={section.width}
                            {...props}
                          ></PageBlock>
                        )
                      },
                    )}
                  </div>
                </div>
              )
            })}
          <MealPlansPageAppPromo topFeatures={pageData.topFeatures} />
          {pageData.faqs && <MealPlansPageFAQ questions={pageData.faqs} />}
        </HashAnchor>
        <PageBottomHero
          nativeAd={nativeAd}
          backgroundStyles={{
            backgroundImage: gradient,
          }}
          heading={pageData.callout}
          mealPlanName={pageData.name}
          readMoreLink={pageData.readMoreLink}
        >
          <img
            src={pageData.calloutImage && pageData.calloutImage.url}
            alt=""
            className="absolute right-0 hidden h-full lg:block"
            style={{
              top: '15%',
            }}
          />
          {pageData.mirrorCalloutImage && (
            <img
              src={pageData.calloutImage && pageData.calloutImage.url}
              alt=""
              className="absolute left-0 hidden h-full lg:block"
              style={{
                bottom: '15%',
                transform: 'scale(-1)',
              }}
            />
          )}
        </PageBottomHero>

        {scrolledBelowFold && <Footer marginless />}
      </div>
    </DefaultLayout>
  )
}

DatoPage.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
}

DatoPage.getInitialProps = ({ location, language }) => {
  const slug = location.pathname.replace(/(\/\w{2})?\//, '').replace(/\//g, '')
  const locale = datoLocales[language]

  return runQuery(mealPlansQuery, { slug, locale }).then(
    ({ page, fallback }) => ({ page, fallback }),
  )
}

export default withSSR(DatoPage)
